import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";

// Editable testimonials block: eyebrow, heading, intro, and a rotating list of
// quotes with author + company + optional logo image. Auto-rotates every 7s
// (pauses on hover), with prev/next arrows and dot indicators.

function StarRating({ size = "sm" }) {
  const px = size === "lg" ? "h-5 w-5" : "h-4 w-4";
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className={`${px} fill-amber-400 text-amber-400`} />
      ))}
    </div>
  );
}

function FeaturedTestimonial({ review, idx }) {
  const logoSrc = review.logo
    ? review.logo.startsWith("http")
      ? review.logo
      : `${process.env.REACT_APP_BACKEND_URL || ""}${review.logo}`
    : "";
  return (
    <motion.div
      key={`featured-${idx}`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className="relative mx-auto max-w-4xl bg-white border border-slate-200 rounded-2xl px-6 py-10 sm:px-12 sm:py-14 shadow-xl shadow-slate-900/5"
      data-testid={`testimonial-featured-${idx}`}
    >
      <Quote className="absolute top-6 right-6 h-16 w-16 text-sky-50" />
      <div className="relative flex flex-col items-center text-center">
        <div className="h-14 flex items-center">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={`${review.company || ""} logo`}
              className="max-h-12 w-auto object-contain"
              loading="lazy"
            />
          ) : (
            <div className="text-xs font-bold uppercase tracking-widest text-slate-400">
              {review.company}
            </div>
          )}
        </div>
        <div className="mt-4">
          <StarRating size="lg" />
        </div>
        <blockquote className="mt-6 text-lg sm:text-xl lg:text-2xl text-slate-700 leading-relaxed font-medium max-w-3xl">
          &ldquo;{review.quote}&rdquo;
        </blockquote>
        <div className="mt-8 flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-sky-100 flex items-center justify-center text-sky-600 text-sm font-bold">
            {(review.author || "").charAt(0)}
          </div>
          <div className="text-left">
            <p className="text-base font-bold text-slate-900">{review.author}</p>
            <p className="text-sm text-slate-500">{review.company}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function TestimonialsRotatorBlock({ data }) {
  const d = data || {};
  const eyebrow = d.eyebrow || "";
  const heading = d.heading || "";
  const intro = d.intro || "";
  const items = Array.isArray(d.items) ? d.items.filter((t) => t && t.quote) : [];

  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || items.length <= 1) return undefined;
    const t = setInterval(() => {
      setActiveIdx((i) => (i + 1) % items.length);
    }, 7000);
    return () => clearInterval(t);
  }, [paused, items.length]);

  if (items.length === 0) return null;

  const next = () => setActiveIdx((i) => (i + 1) % items.length);
  const prev = () => setActiveIdx((i) => (i - 1 + items.length) % items.length);
  const active = items[activeIdx];

  return (
    <section
      data-testid="testimonials-section"
      className="py-16 lg:py-24 bg-slate-50/50 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          {eyebrow && (
            <p className="text-sm font-medium text-slate-500 uppercase tracking-widest mb-3">
              {eyebrow}
            </p>
          )}
          {heading && (
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              {heading}
            </h2>
          )}
          {intro && (
            <p className="mt-3 text-base text-slate-500 max-w-xl mx-auto">{intro}</p>
          )}
        </motion.div>

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="testimonials-featured-wrap"
        >
          <AnimatePresence mode="wait">
            <FeaturedTestimonial key={activeIdx} review={active} idx={activeIdx} />
          </AnimatePresence>

          {items.length > 1 && (
            <>
              <button
                type="button"
                onClick={prev}
                aria-label="Previous testimonial"
                data-testid="testimonials-prev"
                className="hidden lg:flex absolute top-1/2 -translate-y-1/2 -left-2 xl:-left-6 h-11 w-11 rounded-full bg-white ring-1 ring-slate-200 shadow-md items-center justify-center text-slate-500 hover:text-sky-600 hover:ring-sky-200 transition"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next testimonial"
                data-testid="testimonials-next"
                className="hidden lg:flex absolute top-1/2 -translate-y-1/2 -right-2 xl:-right-6 h-11 w-11 rounded-full bg-white ring-1 ring-slate-200 shadow-md items-center justify-center text-slate-500 hover:text-sky-600 hover:ring-sky-200 transition"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>

        {items.length > 1 && (
          <div className="mt-8 flex justify-center gap-2" data-testid="testimonials-dots">
            {items.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActiveIdx(i)}
                aria-label={`Show testimonial ${i + 1}`}
                data-testid={`testimonials-dot-${i}`}
                className={`h-2.5 rounded-full transition-all ${
                  i === activeIdx ? "w-8 bg-sky-600" : "w-2.5 bg-slate-300 hover:bg-slate-400"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
